import { useEffect, useState } from "react";
import { Plus, Edit2, Trash2, Save, X, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import MultiImageUploader from "@/components/MultiImageUploader";

const empty = { name: "", description: "", price: 0, images: [], available: true, tag: "new" };

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [settings, setSettings] = useState(null);

  const load = async () => {
    const [p, s] = await Promise.all([api.get("/products"), api.get("/site-settings")]);
    setProducts(p.data);
    setSettings(s.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const images = (editing.images || []).filter(Boolean);
      const payload = { ...editing, images, price: Number(editing.price), image: images[0] || "" };
      if (editing.id) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Saved");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Deleted"); load();
  };

  const toggleLabel = async () => {
    const next = settings.shop_section_label === "New Arrivals" ? "Upcoming Collection" : "New Arrivals";
    await api.put("/admin/site-settings", { shop_section_label: next });
    toast.success(`Section label set to "${next}"`);
    load();
  };

  const undoImage = async (id) => {
    try { await api.post(`/admin/products/${id}/undo-image`); toast.success("Image restored"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Nothing to restore"); }
  };

  const openEdit = (p) => {
    // Migrate legacy single-image products to images array
    const images = (p.images && p.images.length) ? p.images : (p.image ? [p.image] : []);
    setEditing({ ...p, images });
  };

  return (
    <div data-testid="admin-products">
      <div className="flex justify-between items-end mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-3xl text-brand-soft">Shop</h1>
          <div className="text-sm text-brand-soft/60 mt-1 inline-flex items-center gap-2">
            Section header: <strong className="text-brand-maroon">{settings?.shop_section_label}</strong>
            <button onClick={toggleLabel} className="px-3 py-1 rounded-full bg-brand-ivory text-xs hover:bg-brand-maroon/10" data-testid="toggle-shop-label">Toggle</button>
          </div>
        </div>
        <button onClick={() => setEditing({ ...empty })} className="btn-primary-furshala inline-flex items-center gap-2" data-testid="admin-product-new">
          <Plus className="w-4 h-4" /> New product
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {products.map((p) => {
          const imgs = p.images && p.images.length ? p.images : (p.image ? [p.image] : []);
          return (
            <div key={p.id} className="bg-white rounded-2xl border border-brand-maroon/10 overflow-hidden" data-testid={`admin-product-row-${p.id}`}>
              <div className="aspect-square bg-brand-ivory relative">
                {imgs[0] && <img src={fileUrl(imgs[0])} alt={p.name} className="w-full h-full object-cover" />}
                {imgs.length > 1 && (
                  <div className="absolute bottom-2 right-2 bg-brand-soft/85 text-brand-cream text-[10px] uppercase tracking-widest px-2 py-1 rounded-full">{imgs.length} photos</div>
                )}
              </div>
              <div className="p-4 space-y-1.5">
                <div className="flex justify-between items-center">
                  <div className="font-serif text-lg text-brand-soft">{p.name}</div>
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${p.tag === "upcoming" ? "bg-brand-mustard/25 text-brand-maroon" : "bg-brand-olive/15 text-brand-olive"}`}>{p.tag}</span>
                </div>
                <div className="text-brand-maroon font-medium">₹{p.price}</div>
                <div className="text-xs text-brand-soft/60">{p.available ? "Available" : "Hidden / Sold out"}</div>
                <div className="pt-2 flex flex-wrap gap-1.5">
                  <button onClick={() => openEdit(p)} className="text-xs px-3 py-1.5 rounded-full bg-brand-ivory hover:bg-brand-maroon/10 inline-flex items-center gap-1"><Edit2 className="w-3 h-3" /> Edit</button>
                  {p.previous_image && <button onClick={() => undoImage(p.id)} className="text-xs px-3 py-1.5 rounded-full bg-brand-ivory hover:bg-brand-maroon/10 inline-flex items-center gap-1"><Undo2 className="w-3 h-3" /> Undo image</button>}
                  <button onClick={() => remove(p.id)} className="text-xs px-3 py-1.5 rounded-full bg-red-50 text-red-700 hover:bg-red-100 inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-3xl max-w-2xl w-full p-7 sm:p-10" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl text-brand-soft">{editing.id ? "Edit product" : "New product"}</h2>
              <button onClick={() => setEditing(null)} className="p-2"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block md:col-span-2"><div className="text-xs uppercase tracking-widest text-brand-soft/60 mb-1">Name</div>
                <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="w-full bg-brand-ivory rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-mustard/40" data-testid="pr-name" />
              </label>
              <label className="block md:col-span-2"><div className="text-xs uppercase tracking-widest text-brand-soft/60 mb-1">Description</div>
                <textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={2} className="w-full bg-brand-ivory rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-mustard/40" />
              </label>
              <label className="block"><div className="text-xs uppercase tracking-widest text-brand-soft/60 mb-1">Price ₹</div>
                <input type="number" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className="w-full bg-brand-ivory rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-mustard/40" data-testid="pr-price" />
              </label>
              <label className="block"><div className="text-xs uppercase tracking-widest text-brand-soft/60 mb-1">Tag</div>
                <select value={editing.tag} onChange={(e) => setEditing({ ...editing, tag: e.target.value })} className="w-full bg-brand-ivory rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-mustard/40">
                  <option value="new">New arrival</option>
                  <option value="upcoming">Upcoming</option>
                </select>
              </label>
              <label className="flex items-center gap-2 text-sm md:col-span-2">
                <input type="checkbox" checked={editing.available} onChange={(e) => setEditing({ ...editing, available: e.target.checked })} className="accent-brand-maroon" />
                Available for purchase (show Buy button)
              </label>
              <div className="md:col-span-2">
                <MultiImageUploader
                  label="Product images"
                  recommended="(recommended 800×800, multiple supported — first is primary)"
                  value={editing.images || []}
                  onChange={(v) => setEditing({ ...editing, images: v })}
                />
              </div>
            </div>
            <div className="mt-8 flex gap-3 justify-end">
              <button onClick={() => setEditing(null)} className="btn-outline-furshala">Cancel</button>
              <button onClick={save} className="btn-primary-furshala inline-flex items-center gap-2" data-testid="pr-save"><Save className="w-4 h-4" /> Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
